import React, { useState } from 'react';
import { WidgetSettings } from '../../services/widgetSettingsService';
import widgetSettingsService from '../../services/widgetSettingsService';

interface WidgetPreviewProps {
  settings: WidgetSettings;
  className?: string;
}

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  settings,
  className = ''
}) => {
  const [isMinimized, setIsMinimized] = useState(true);

  // Process template variables
  const processedWelcomeMessage = widgetSettingsService.processMessageTemplate(
    settings.welcome_message || 'Hi! Welcome to {{school_name}}. How can I help you today?',
    settings
  );

  const cssVariables = widgetSettingsService.generateCSSVariables(settings);
  
  const avatarUrl = settings.avatar_url 
    ? widgetSettingsService.getImageUrl(settings.avatar_url)
    : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM0RjQ2RTUiLz48L3N2Zz4=';

  return (
    <div className={`bg-gray-100 rounded-lg p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
        <p className="text-sm text-gray-600">See how your widget will look to visitors</p>
      </div>

      {/* Preview Container */}
      <div 
        className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-8 min-h-[400px] overflow-hidden"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
      >
        {/* Mock Website Content */}
        <div className="absolute inset-4 bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
          </div>
        </div>

        {/* Widget */}
        <div 
          className={`absolute ${settings.position === 'bottom-left' ? 'bottom-4 left-4' : 'bottom-4 right-4'}`}
          style={cssVariables as React.CSSProperties}
        >
          {isMinimized ? (
            /* Chat Button */
            <div className="relative">
              <button
                onClick={() => setIsMinimized(false)}
                className="w-16 h-16 rounded-full shadow-lg text-white flex items-center justify-center hover:scale-105 transition-transform"
                style={{ 
                  background: `linear-gradient(135deg, ${settings.primary_color || '#4F46E5'} 0%, ${settings.secondary_color || '#7C3AED'} 100%)`,
                  borderRadius: settings.border_radius || '50%'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C17.5 2 22 6.5 22 12C22 17.5 17.5 22 12 22H2L12 2Z" fill="currentColor"/>
                </svg>
              </button>
              
              {/* Call to Action */}
              {settings.call_to_action_text && (
                <div 
                  className="absolute bottom-full right-0 mb-2 px-3 py-2 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap"
                  style={{ 
                    backgroundColor: settings.background_color || '#FFFFFF',
                    color: settings.text_color || '#1F2937',
                    border: `1px solid ${settings.primary_color || '#4F46E5'}20`
                  }}
                >
                  {settings.call_to_action_text}
                  <div 
                    className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                    style={{ borderTopColor: settings.background_color || '#FFFFFF' }}
                  ></div>
                </div>
              )}
            </div>
          ) : (
            /* Chat Window */
            <div 
              className="w-80 h-96 rounded-lg shadow-xl flex flex-col overflow-hidden"
              style={{ 
                backgroundColor: settings.background_color || '#FFFFFF',
                borderRadius: settings.border_radius || '12px'
              }}
            >
              {/* Header */}
              <div 
                className="p-4 text-white flex items-center justify-between"
                style={{ 
                  background: `linear-gradient(135deg, ${settings.primary_color || '#4F46E5'} 0%, ${settings.secondary_color || '#7C3AED'} 100%)`
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/20">
                    <img 
                      src={avatarUrl} 
                      alt="Assistant" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {settings.chatbot_name || 'School Assistant'}
                    </div>
                    <div className="text-xs opacity-90">Online</div>
                  </div>
                </div>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="text-white/80 hover:text-white text-lg"
                >
                  ×
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 space-y-3 bg-gray-50">
                <div className="flex">
                  <div 
                    className="max-w-xs px-3 py-2 rounded-lg text-sm"
                    style={{ 
                      backgroundColor: settings.background_color || '#FFFFFF',
                      color: settings.text_color || '#1F2937',
                      borderRadius: '12px 12px 12px 4px'
                    }}
                  >
                    {processedWelcomeMessage}
                  </div>
                </div>
              </div>

              {/* Input */}
              <div className="p-4 border-t border-gray-200">
                <div 
                  className="flex items-center space-x-2 bg-gray-100 rounded-full px-3 py-2"
                >
                  <input
                    type="text"
                    placeholder="Type your message..."
                    className="flex-1 bg-transparent text-sm outline-none"
                    style={{ color: settings.text_color || '#1F2937' }}
                  />
                  <button 
                    className="w-8 h-8 rounded-full text-white flex items-center justify-center"
                    style={{ backgroundColor: settings.primary_color || '#4F46E5' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M18 10L2 10M18 10L12 4M18 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Position: <span className="font-medium">{settings.position || 'bottom-right'}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
          >
            {isMinimized ? 'Expand' : 'Minimize'}
          </button>
          <a
            href={widgetSettingsService.getWidgetPreviewUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            View Full Demo →
          </a>
        </div>
      </div>

      {/* Settings Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-600 space-y-1">
        <div><span className="font-medium">School:</span> {settings.school_name || 'Not set'}</div>
        <div><span className="font-medium">Chatbot:</span> {settings.chatbot_name || 'Not set'}</div>
        <div className="flex items-center space-x-2">
          <span className="font-medium">Colors:</span>
          <div className="flex space-x-1">
            <div 
              className="w-4 h-4 rounded border border-gray-300" 
              style={{ backgroundColor: settings.primary_color || '#4F46E5' }}
              title="Primary"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-300" 
              style={{ backgroundColor: settings.secondary_color || '#7C3AED' }}
              title="Secondary"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-300" 
              style={{ backgroundColor: settings.accent_color || '#10B981' }}
              title="Accent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPreview;